import { TerminalState } from "../../../../slices/terminal-summary/TerminalDetailsState";
import { useSelector } from "react-redux";
import { getTerminalSummaryByTerminalNumber, terminalDetailsSelector } from "../../../../slices/terminal-summary/terminalDetailsSlice";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Form, Formik, FormikProps } from "formik";
import AddressForm from "../../../common/form/address";
import { Address, getAddressValidation, getPhysicalAddressValidation } from "../../../../app/data/terminal-details/models";
import React from "react";
import ActionSection from "features/terminal-details/common/actionSection";
import { updateAddressSelector, updateTerminalAddressById } from "../../../../slices/terminal-summary/addressSlice";
import { UpdateAddressState } from "../../../../slices/terminal-summary/AddressState";
import _ from "lodash";
import { toast } from "react-toastify";

const EditAddressDetails = ({ onClose }: { onClose: () => void }): React.ReactElement => {
    const [spinner, setSpinner] = useState<boolean>(false);
    const terminalState: TerminalState = useSelector(terminalDetailsSelector);
    const updateAddressState: UpdateAddressState = useSelector(updateAddressSelector);
    const dispatch = useDispatch();
    const [isSameAsPhysical, setIsSameAsPhysical] = useState<boolean>(terminalState.data?.address?.isMailingAddressSame || false);

    useEffect(() => {
        if (updateAddressState.isSuccess) {
            dispatch(getTerminalSummaryByTerminalNumber(terminalState.data?.terminalNumber?.toString() || ""));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateAddressState, dispatch]);

    useEffect(() => {
        if (!terminalState.isLoading) {
            setIsSameAsPhysical(terminalState.data?.address?.isMailingAddressSame || false);
        }
    }, [terminalState]);

    const handleClear = () => {
        formRef.current?.resetForm();
        setIsSameAsPhysical(terminalState.data?.address?.isMailingAddressSame || false); 
    };

    const handleSpinner = async () => {
        setTimeout(() => {
            setSpinner(false);
        }, 1000);
    };

    const handleCheckboxChange = (isSameAsPhysical: boolean) => {
        if (isSameAsPhysical) {
            formRef.current?.setValues({
                ...formRef.current?.values,
                mailingAddress: formRef.current?.values.street,
                mailingCity: formRef.current?.values.city,
                mailingStateProvince: formRef.current?.values.state,
                mailingZipPostalCode: formRef.current?.values.zip,
                mailingCountry: formRef.current?.values.country,
                isMailingAddressSame: true
            });
        }
        else {
            formRef.current?.setValues({
                ...formRef.current?.values,
                mailingAddress: "",
                mailingCity: "",
                mailingStateProvince: "",
                mailingZipPostalCode: "",
                mailingCountry: "",
                isMailingAddressSame: false
            });
        }
    };

    const initialValues: Address = { street: terminalState.data?.address?.physicalAddress || '', 
        city: terminalState.data?.address?.city || '', 
        state: terminalState.data?.address?.stateProvince || '', 
        zip: terminalState.data?.address?.zipPostalCode || '', 
        country: terminalState.data?.address?.country || '',
        mailingAddress: terminalState.data?.address?.mailingAddress || '',
        mailingCity: terminalState.data?.address?.mailingCity || '',
        mailingStateProvince: terminalState.data?.address?.mailingStateProvince || '',
        mailingZipPostalCode: terminalState.data?.address?.mailingZipPostalCode || '',
        mailingCountry: terminalState.data?.address?.mailingCountry || '',
        isMailingAddressSame: terminalState.data?.address?.isMailingAddressSame || false,
     };

    const formRef = useRef<FormikProps<Address>>(null);
    return <>
        <div className="form-heading">Physical Address</div>
        <Formik
            onSubmit={() => { }}
            initialValues={initialValues}
            validationSchema={isSameAsPhysical ? getPhysicalAddressValidation() : getAddressValidation()}
            innerRef={formRef}
            enableReinitialize={true}
        >
            {(formikProps: FormikProps<Address>) => (
                <Form>
                    <AddressForm />
                    <div className="form-checkbox">
                        <label>
                            <input
                                type="checkbox"
                                className="form-checkbox__input"
                                checked={isSameAsPhysical}
                                onChange={() => {
                                    const checked = !isSameAsPhysical;
                                    setIsSameAsPhysical(checked);
                                    handleCheckboxChange(checked);
                                    formikProps.setFieldValue("isMailingAddressSame", checked);
                                }}
                            />
                            {" "}
                        </label>
                        <div>Mailing address same as physical</div>
                    </div>
                    <div className="form-heading">Mailing Address</div>
                    <AddressForm isSameAsPhysical={isSameAsPhysical} isMailingAddress={true}/>
                    <div className="action-section">
                    <ActionSection
                        handleClear={() => { handleClear() }}
                        handleSave={async () => {
                            const values = formRef.current?.values;
                            setSpinner(true);
                            await handleSpinner();

                            await Promise.all([
                                formRef?.current?.submitForm(),
                            ]);

                            if (terminalState.data?.terminalNumber && terminalState.data?.address?.id && _.isEmpty(formRef.current?.errors)) {
                                dispatch(updateTerminalAddressById(terminalState.data?.terminalNumber?.toString() || "", terminalState.data?.address?.id || "", {
                                    physicalAddress: values?.street || "",
                                    city: values?.city || "",
                                    stateProvince: values?.state || "",
                                    zipPostalCode: values?.zip || "",
                                    country: values?.country || "",
                                    mailingAddress: values?.mailingAddress || "",
                                    mailingCity: values?.mailingCity || "",
                                    mailingStateProvince: values?.mailingStateProvince || "",
                                    mailingZipPostalCode: values?.mailingZipPostalCode || "",
                                    mailingCountry: values?.mailingCountry || "",
                                    isMailingAddressSame: isSameAsPhysical
                                }, () => {
                                    toast.info("Updated details");
                                }, (message) => {
                                    toast.error(message);
                                }));
                            }
                        }}
                        spinner={updateAddressState?.isLoading || spinner}
                        disabled={!formikProps.dirty}
                    />
                    </div>
                </Form>
            )}
        </Formik>
    </>
}



export default EditAddressDetails;
